'use client';

import { type FC, type MouseEvent, useCallback, useEffect, useState } from 'react';
import { BottomSheet, Button } from '../ui';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useCapability } from '@/runtime/useCapability';
import { FaCopy, FaShare } from '../icons';
import translations from './InviteSheet.translations';

interface InviteSheetProps {
  isOpen: boolean;
  onClose: () => void;
  link: string;
  onShare: () => void;
  isSharing?: boolean;
}

type CopyState = 'idle' | 'copied' | 'failed';

function selectWholeNode(event: MouseEvent<HTMLElement>): void {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.selectNodeContents(event.currentTarget);
  selection.removeAllRanges();
  selection.addRange(range);
}

export const InviteSheet: FC<InviteSheetProps> = ({
  isOpen,
  onClose,
  link,
  onShare,
  isSharing,
}) => {
  const { t } = useLocalTranslation(translations);
  const canNativeShare = useCapability('nativeShare');
  const [copyState, setCopyState] = useState<CopyState>('idle');

  useEffect(() => {
    if (!isOpen) setCopyState('idle');
  }, [isOpen]);

  useEffect(() => {
    if (copyState !== 'copied') return;
    const timer = setTimeout(() => setCopyState('idle'), 2000);
    return () => clearTimeout(timer);
  }, [copyState]);

  const handleCopy = useCallback(async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  }, [link]);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={t('title')}
      heightClass="h-auto"
    >

      <div className="flex flex-col gap-3 px-4 pt-1 pb-4">
        {canNativeShare && (
          <Button
            size="l"
            stretched
            variant="cta"
            onClick={onShare}
            loading={isSharing}
            disabled={!link}
            before={<FaShare />}
          >
            {t('share')}
          </Button>
        )}


        <Button
          size="l"
          stretched
          variant="secondary"
          onClick={handleCopy}
          disabled={!link}
          before={<FaCopy />}
          data-qa="invite-copy"
        >
          {copyState === 'copied' ? t('copied') : t('copyLink')}
        </Button>

        {link && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-label-tertiary">{t('linkLabel')}</span>

            <p
              onClick={selectWholeNode}
              className="m-0 w-full cursor-text select-all rounded-xl bg-fill-tertiary px-3 py-2.5 text-sm break-all text-label-primary"
            >
              {link}
            </p>
            {copyState === 'failed' && (
              <span className="text-xs font-medium text-accent-red">{t('copyFailed')}</span>
            )}
          </div>
        )}
      </div>
    </BottomSheet>
  );
};
